import React, { useState, useEffect, useCallback, useRef } from 'react';
import Topbar from '../components/Topbar';
import { 
    getAlertRules, 
    createAlertRule, 
    deleteAlertRule, 
    getTop100Airports, 
    syncAirportAlerts, 
    getAlertLogs, 
    clearAlertLogs 
} from '../services/flightAlertService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';

export default function FlightAlerts() {
    const [rules, setRules] = useState([]);
    const [airports, setAirports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncingAirport, setSyncingAirport] = useState(false);
    const [creatingRule, setCreatingRule] = useState(false);

    // Server-side pagination & filter states
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [directionFilter, setDirectionFilter] = useState('ALL');

    // Sync full airport state
    const [selectedSyncAirport, setSelectedSyncAirport] = useState('CMH');
    const [syncDirection, setSyncDirection] = useState('both');
    const [syncDate, setSyncDate] = useState(() => new Date().toISOString().split('T')[0]);

    // Single rule state
    const [carrier, setCarrier] = useState('');
    const [flightNumber, setFlightNumber] = useState('');
    const [ruleAirport, setRuleAirport] = useState('CMH');
    const [ruleDirection, setRuleDirection] = useState('departure');
    const [ruleDate, setRuleDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [deliverTo, setDeliverTo] = useState('https://api.gateguide.io/api/flights/webhook/alerts');

    // Logs state
    const [logs, setLogs] = useState('');
    const [autoRefreshLogs, setAutoRefreshLogs] = useState(true);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const logsEndRef = useRef(null);

    // Fetch alert rules with SERVER-SIDE pagination
    const fetchRules = useCallback(async (page = 1, limit = 10, search = '', status = 'ALL', direction = 'ALL') => {
        try {
            setLoading(true);
            const res = await getAlertRules({
                page,
                limit,
                search: search.trim(),
                status: status === 'ALL' ? '' : status,
                direction: direction === 'ALL' ? '' : direction
            });

            const data = res?.data || {};
            setRules(data.rules || []);
            setTotalRecords(data.total || 0);
            setTotalPages(data.totalPages || 1);
            setCurrentPage(data.page || 1);
        } catch (err) {
            console.error('Error fetching alert rules:', err);
            toast.error('Failed to load alert rules');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial airports fetch
    useEffect(() => {
        getTop100Airports()
            .then(res => {
                const list = res?.data?.airports || res?.airports || [];
                setAirports(list);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        fetchRules(currentPage, pageSize, searchQuery, statusFilter, directionFilter);
    }, [fetchRules, currentPage, pageSize, statusFilter, directionFilter]);

    // Logs fetch
    const fetchLogs = useCallback(async () => {
        try {
            setLoadingLogs(true);
            const res = await getAlertLogs();
            const logContent = res?.data?.logs || res?.logs || 'No log entries found yet.';
            setLogs(logContent);
        } catch (err) {
            console.error('Error fetching logs:', err);
        } finally {
            setLoadingLogs(false);
        }
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        if (!autoRefreshLogs) return;
        const interval = setInterval(() => {
            fetchLogs();
        }, 8000);
        return () => clearInterval(interval);
    }, [autoRefreshLogs, fetchLogs]);

    // Search debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchRules(1, pageSize, searchQuery, statusFilter, directionFilter);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchQuery, pageSize, statusFilter, directionFilter, fetchRules]);

    // Handle full airport sync
    const handleSyncAirport = async (e) => {
        e.preventDefault();
        try {
            setSyncingAirport(true);
            toast.info(`Starting alert sync for ${selectedSyncAirport}... Please wait.`);
            const res = await syncAirportAlerts({
                airport: selectedSyncAirport,
                type: syncDirection,
                date: syncDate,
                deliver_to: deliverTo
            });

            const msg = res?.message || `Sync completed for ${selectedSyncAirport}!`;
            toast.success(msg);
            fetchRules(1, pageSize, searchQuery, statusFilter, directionFilter);
            fetchLogs();
        } catch (err) {
            console.error('Sync airport error:', err);
            toast.error(err.response?.data?.message || 'Airport alert sync failed.');
        } finally {
            setSyncingAirport(false);
        }
    };

    // Handle create single rule
    const handleCreateRule = async (e) => {
        e.preventDefault();
        if (!carrier || !flightNumber) {
            toast.warning('Please enter both Airline code (e.g. DL) and Flight Number.');
            return;
        }

        try {
            setCreatingRule(true);
            const res = await createAlertRule({
                carrier: carrier.trim().toUpperCase(),
                flight_number: flightNumber.trim(),
                airport: ruleAirport,
                direction: ruleDirection,
                date: ruleDate,
                deliver_to: deliverTo
            });

            toast.success(res?.message || 'Alert rule registered successfully!');
            setFlightNumber('');
            fetchRules(1, pageSize, searchQuery, statusFilter, directionFilter);
            fetchLogs();
        } catch (err) {
            console.error('Create rule error:', err);
            toast.error(err.response?.data?.message || 'Failed to create alert rule.');
        } finally {
            setCreatingRule(false);
        }
    };

    // Handle delete rule
    const handleDeleteRule = async (rule) => {
        const idToDelete = rule.rule_id || rule.id;
        if (!window.confirm(`Are you sure you want to delete rule for ${rule.carrier} ${rule.flight_number}?`)) {
            return;
        }

        try {
            await deleteAlertRule(idToDelete);
            toast.success(`Rule for ${rule.carrier} ${rule.flight_number} deleted.`);
            fetchRules(currentPage, pageSize, searchQuery, statusFilter, directionFilter);
        } catch (err) {
            console.error('Delete rule error:', err);
            toast.error('Failed to delete alert rule.');
        }
    };

    const handleClearLogs = async () => {
        if (!window.confirm('Clear all webhook log entries?')) return;
        try {
            await clearAlertLogs();
            setLogs('Logs cleared.');
            toast.success('Logs cleared.');
        } catch (err) {
            toast.error('Failed to clear logs.');
        }
    };

    const renderPagination = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 3) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                pages.push(currentPage - 1);
                pages.push(currentPage);
                pages.push(currentPage + 1);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    return (
        <div>
            <Topbar title="Flight Alerts" subtitle="Direct Cirium Push Alert Rules and Live Webhook Monitoring" />

            <div className="content">
                {/* TOP STATS CARDS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                            Total Rules in Database
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--navy)', marginTop: 6, fontFamily: "'Space Grotesk', sans-serif" }}>
                            {totalRecords}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 4, fontWeight: 600 }}>
                            Complete database coverage
                        </div>
                    </div>

                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                            Live Top Airports
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue)', marginTop: 6, fontFamily: "'Space Grotesk', sans-serif" }}>
                            {airports.length || 100}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--grey)', marginTop: 4, fontWeight: 600 }}>
                            US Hubs & Regional Airports
                        </div>
                    </div>

                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                            Cirium Push Target
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', marginTop: 10, fontFamily: "'Space Grotesk', sans-serif" }}>
                            api.gateguide.io
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 6, fontWeight: 600 }}>
                            ● Webhook Listener Ready
                        </div>
                    </div>

                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
                            Auto Cron Scanner
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--green)', marginTop: 6, fontFamily: "'Space Grotesk', sans-serif" }}>
                            Every 30 Mins
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--grey)', marginTop: 4, fontWeight: 600 }}>
                            CMH + Live Airport Hubs
                        </div>
                    </div>
                </div>

                {/* TWO ACTION FORMS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 24, marginBottom: 28 }}>
                    
                    {/* 1. Full Airport Batch Sync Card */}
                    <div className="card" style={{ padding: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EEF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2.2">
                                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                                </svg>
                            </div>
                            <div>
                                <h3 className="card-title" style={{ margin: 0 }}>Sync Entire Airport Alert Coverage</h3>
                                <p style={{ margin: 0, fontSize: 12, color: 'var(--grey)', marginTop: 2 }}>
                                    Fetch all daily arrivals & departures from Cirium and register alert rules.
                                </p>
                            </div>
                        </div>

                        <form onSubmit={handleSyncAirport} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Select Airport</label>
                                <select 
                                    className="filter-select"
                                    style={{ width: '100%' }}
                                    value={selectedSyncAirport} 
                                    onChange={(e) => setSelectedSyncAirport(e.target.value)}
                                >
                                    {airports.map(apt => (
                                        <option key={apt.id || apt.code} value={apt.code}>
                                            #{apt.rank || '-'} {apt.code} - {apt.name} ({apt.city}, {apt.state})
                                        </option>
                                    ))}
                                    {airports.length === 0 && (
                                        <option value="CMH">CMH - John Glenn Columbus International</option>
                                    )}
                                </select>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Flight Type</label>
                                    <select 
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={syncDirection} 
                                        onChange={(e) => setSyncDirection(e.target.value)}
                                    >
                                        <option value="both">Both (Departures & Arrivals)</option>
                                        <option value="departure">Departures Only</option>
                                        <option value="arrival">Arrivals Only</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Date</label>
                                    <input 
                                        type="date" 
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={syncDate} 
                                        onChange={(e) => setSyncDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                disabled={syncingAirport}
                                className="btn btn-primary"
                                style={{ marginTop: 6, justifyContent: 'center' }}
                            >
                                {syncingAirport ? <Spinner size="sm" /> : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <polyline points="23 4 23 10 17 10"/>
                                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                                    </svg>
                                )}
                                {syncingAirport ? 'Syncing Full Airport Flights...' : `Sync All ${selectedSyncAirport} Flights Now`}
                            </button>
                        </form>
                    </div>

                    {/* 2. Single Flight Alert Rule Form */}
                    <div className="card" style={{ padding: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#FFFBE6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B8860B" strokeWidth="2.2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                            </div>
                            <div>
                                <h3 className="card-title" style={{ margin: 0 }}>Register Single Flight Alert</h3>
                                <p style={{ margin: 0, fontSize: 12, color: 'var(--grey)', marginTop: 2 }}>
                                    Create a targeted Cirium Sky rule by airline code and flight number.
                                </p>
                            </div>
                        </div>

                        <form onSubmit={handleCreateRule} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Airline (IATA)</label>
                                    <input 
                                        type="text" 
                                        className="search-input"
                                        placeholder="e.g. DL, UA, AA" 
                                        value={carrier} 
                                        onChange={(e) => setCarrier(e.target.value.toUpperCase())}
                                        maxLength={4}
                                        required 
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Flight Number</label>
                                    <input 
                                        type="text" 
                                        className="search-input"
                                        placeholder="e.g. 5261 or 1182" 
                                        value={flightNumber} 
                                        onChange={(e) => setFlightNumber(e.target.value)}
                                        required 
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Airport</label>
                                    <select 
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={ruleAirport} 
                                        onChange={(e) => setRuleAirport(e.target.value)}
                                    >
                                        {airports.map(apt => (
                                            <option key={apt.id || apt.code} value={apt.code}>
                                                {apt.code}
                                            </option>
                                        ))}
                                        {airports.length === 0 && <option value="CMH">CMH</option>}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Direction</label>
                                    <select 
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={ruleDirection} 
                                        onChange={(e) => setRuleDirection(e.target.value)}
                                    >
                                        <option value="departure">Departure</option>
                                        <option value="arrival">Arrival</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 6 }}>Date</label>
                                    <input 
                                        type="date" 
                                        className="filter-select"
                                        style={{ width: '100%' }}
                                        value={ruleDate} 
                                        onChange={(e) => setRuleDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                disabled={creatingRule}
                                className="btn btn-primary"
                                style={{ marginTop: 6, justifyContent: 'center' }}
                            >
                                {creatingRule ? <Spinner size="sm" /> : '+'}
                                {creatingRule ? 'Creating Rule on Cirium...' : 'Register Cirium Alert Rule'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* RULES TABLE CARD */}
                <div className="card">
                    {/* TOOLBAR */}
                    <div className="toolbar">
                        <div className="search-wrap">
                            <svg viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2" strokeLinecap="round">
                                <circle cx="11" cy="11" r="8"/>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            <input 
                                type="text"
                                className="search-input"
                                placeholder="Search by airline, flight number, airport, or rule ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        {/* Status Filter */}
                        <select 
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="ALL">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="deleted">Deleted</option>
                        </select>

                        {/* Direction Filter */}
                        <select 
                            className="filter-select"
                            value={directionFilter}
                            onChange={(e) => { setDirectionFilter(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="ALL">All Directions</option>
                            <option value="departure">Departures Only</option>
                            <option value="arrival">Arrivals Only</option>
                        </select>

                        {(searchQuery || statusFilter !== 'ALL' || directionFilter !== 'ALL') && (
                            <button 
                                type="button"
                                className="btn btn-outline"
                                onClick={() => {
                                    setSearchQuery('');
                                    setStatusFilter('ALL');
                                    setDirectionFilter('ALL');
                                    setCurrentPage(1);
                                }}
                            >
                                Reset
                            </button>
                        )}

                        <button 
                            type="button"
                            className="btn btn-outline"
                            onClick={() => fetchRules(currentPage, pageSize, searchQuery, statusFilter, directionFilter)}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="23 4 23 10 17 10"/>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                            </svg>
                            Refresh
                        </button>
                    </div>

                    {/* TABLE */}
                    {loading && rules.length === 0 ? (
                        <div style={{ padding: 60, textAlign: 'center' }}>
                            <Spinner />
                        </div>
                    ) : rules.length === 0 ? (
                        <div style={{ padding: 60, textAlign: 'center', color: 'var(--grey)' }}>
                            <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--dark)' }}>No Cirium alert rules found</div>
                            <div style={{ fontSize: 13, marginTop: 4 }}>Sync an airport above or register an alert rule.</div>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="tbl">
                                <thead>
                                    <tr>
                                        <th>Rule ID</th>
                                        <th>Flight</th>
                                        <th>Airport</th>
                                        <th>Direction</th>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th>Deliver To</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rules.map((rule) => {
                                        return (
                                            <tr key={rule.id}>
                                                <td>
                                                    <span style={{
                                                        fontFamily: 'monospace',
                                                        fontSize: 12,
                                                        background: '#F0F4F8',
                                                        color: 'var(--dark)',
                                                        padding: '3px 8px',
                                                        borderRadius: 6
                                                    }}>
                                                        {rule.rule_id || `DB-#${rule.id}`}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 13 }}>
                                                        {rule.carrier} {rule.flight_number}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{
                                                        background: '#FFFBE6',
                                                        color: '#B8860B',
                                                        fontWeight: 800,
                                                        padding: '3px 8px',
                                                        borderRadius: 6,
                                                        fontSize: 12
                                                    }}>
                                                        {rule.airport}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge badge-admin" style={{ textTransform: 'capitalize' }}>
                                                        {rule.direction}
                                                    </span>
                                                </td>
                                                <td style={{ color: 'var(--dark)', fontSize: 13 }}>
                                                    {rule.flight_date}
                                                </td>
                                                <td>
                                                    {rule.status === 'active' && (
                                                        <span className="badge badge-active">
                                                            Active
                                                        </span>
                                                    )}
                                                    {rule.status === 'deleted' && (
                                                        <span className="badge badge-inactive">
                                                            Deleted
                                                        </span>
                                                    )}
                                                    {rule.status !== 'active' && rule.status !== 'deleted' && (
                                                        <span className="badge badge-soon">
                                                            {rule.status || 'Pending'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ color: 'var(--grey)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={rule.deliver_to || ''}>
                                                    {rule.deliver_to ? rule.deliver_to.replace('https://', '') : 'Default Webhook'}
                                                </td>
                                                <td>
                                                    <div className="action-btns" style={{ justifyContent: 'flex-end' }}>
                                                        <button
                                                            className="action-btn danger"
                                                            onClick={() => handleDeleteRule(rule)}
                                                            title="Delete rule"
                                                        >
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <polyline points="3 6 5 6 21 6"/>
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* PAGINATION FOOTER */}
                    {totalRecords > 0 && (
                        <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid #F0F4F8' }}>
                            <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((currentPage - 1) * pageSize) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(currentPage * pageSize, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> rules
                            </div>

                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <button
                                    className="btn btn-outline"
                                    disabled={currentPage === 1 || loading}
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                >
                                    Previous
                                </button>

                                <div style={{ display: 'flex', gap: 4 }}>
                                    {renderPagination().map((p, i) => (
                                        <button
                                            key={i}
                                            className={`btn ${currentPage === p ? 'btn-primary' : 'btn-outline'}`}
                                            onClick={() => typeof p === 'number' && setCurrentPage(p)}
                                            style={{
                                                padding: '6px 12px',
                                                fontSize: 12,
                                                minWidth: 36,
                                                cursor: typeof p === 'number' ? 'pointer' : 'default',
                                                border: typeof p === 'number' ? '' : 'none',
                                                background: typeof p === 'number' ? '' : 'transparent'
                                            }}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="btn btn-outline"
                                    disabled={currentPage >= totalPages || loading}
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* LIVE WEBHOOK LOG VIEWER */}
                <div className="card" style={{ marginTop: 24, padding: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div>
                            <h3 className="card-title" style={{ margin: 0 }}>
                                Live Cirium Webhook Logs
                            </h3>
                            <p style={{ margin: 0, fontSize: 12, color: 'var(--grey)', marginTop: 2 }}>
                                Real-time push events delivered to /api/flights/webhook/alerts
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => setAutoRefreshLogs(!autoRefreshLogs)}
                            >
                                Auto-Refresh: {autoRefreshLogs ? 'ON (8s)' : 'PAUSED'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleClearLogs}
                            >
                                Clear Logs
                            </button>
                        </div>
                    </div>

                    <div style={{
                        background: '#0A1F44',
                        color: '#2ECC71',
                        borderRadius: 12,
                        padding: '16px 20px',
                        fontFamily: 'monospace',
                        fontSize: 12,
                        maxHeight: 280,
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6
                    }}>
                        {loadingLogs ? 'Fetching logs...' : logs}
                        <div ref={logsEndRef} />
                    </div>
                </div>
            </div>
        </div>
    );
}
